import { COUNTRIES_BY_CODE, CountryCode } from "@redotech/locale/countries";
import {
  CANADA_PROVINCES_BY_CODE,
  CANADA_PROVINCE_CODES,
  USA_PROVINCES_BY_CODE,
  USA_PROVINCE_CODES,
} from "@redotech/locale/provinces";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { CustomerAddressState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-address";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";

interface ProvinceData {
  codes: readonly string[];
  byCode: Record<string, string>;
}

const PROVINCE_DATA_BY_COUNTRY: Record<string, ProvinceData> = {
  US: { codes: USA_PROVINCE_CODES, byCode: USA_PROVINCES_BY_CODE },
  CA: { codes: CANADA_PROVINCE_CODES, byCode: CANADA_PROVINCES_BY_CODE },
};

const SUPPORTED_COUNTRIES: CountryCode[] = ["US", "CA"];

export const CustomerAddressDetails = memo(function CustomerAddressDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<CustomerAddressState>) {
  const [searchString, setSearchString] = useState("");

  const countryOptions: BadgeRedoListItem<CountryCode>[] = useMemo(() => {
    return SUPPORTED_COUNTRIES.map((code) => ({
      id: code,
      value: code,
      type: "badge" as const,
      badge: { text: COUNTRIES_BY_CODE[code], color: "gray" as const },
    }));
  }, []);

  const selectedCountry: BadgeRedoListItem<CountryCode> | undefined =
    useMemo(() => {
      if (!state.country) {
        return undefined;
      }
      return {
        id: state.country as CountryCode,
        value: state.country as CountryCode,
        type: "badge" as const,
        badge: {
          text:
            COUNTRIES_BY_CODE[state.country as CountryCode] || state.country,
          color: "gray" as const,
        },
      };
    }, [state.country]);

  const provinceData = PROVINCE_DATA_BY_COUNTRY[state.country];

  const provinceOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!provinceData) {
      return [];
    }
    return provinceData.codes.map((code) => ({
      id: code,
      value: code,
      type: "badge" as const,
      badge: {
        text: provinceData.byCode[code] || code,
        color: "gray" as const,
      },
    }));
  }, [provinceData]);

  const filteredProvinceOptions = useMemo(() => {
    if (!searchString) {
      return provinceOptions;
    }
    const searchLower = searchString.toLowerCase();
    return provinceOptions.filter((option) => {
      const text = option.badge.text;
      const id = option.id;
      return (
        (typeof text === "string" &&
          text.toLowerCase().includes(searchLower)) ||
        (typeof id === "string" && id.toLowerCase().includes(searchLower))
      );
    });
  }, [provinceOptions, searchString]);

  const selectedProvinces: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!provinceData) {
      return [];
    }
    return [...state.provinceCodes].map((provinceCode) => ({
      id: provinceCode,
      value: provinceCode,
      type: "badge" as const,
      badge: {
        text: provinceData.byCode[provinceCode] || provinceCode,
        color: "gray" as const,
      },
    }));
  }, [provinceData, state.provinceCodes]);

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        label="Country"
        options={countryOptions}
        optionSelected={(item) => {
          setState({ country: item.item.value, provinceCodes: [] });
        }}
        placeholder="Select country..."
        selectedItem={selectedCountry}
        size="xs"
      />
      {provinceData && (
        <RedoTagsDropdownInput
          disabled={disabled}
          label={state.country === "US" ? "States" : "Provinces"}
          options={filteredProvinceOptions}
          placeholder={
            state.country === "US" ? "Select states..." : "Select provinces..."
          }
          searchPlaceholder="Search..."
          searchString={searchString}
          selectedOptions={selectedProvinces}
          setSearchString={setSearchString}
          setSelectedOptions={(items) => {
            setState({
              country: state.country,
              provinceCodes: items.map((item) => item.value),
            });
          }}
          size="xs"
        />
      )}
    </Flex>
  );
});
